import React, { ReactElement, useEffect, useRef, useState, RefObject } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import moment from 'moment';
import Collapse from 'react-bootstrap/Collapse';
import { Sidebar } from 'primereact/sidebar';
import { Avatar } from 'primereact/avatar';
import { Skeleton } from 'primereact/skeleton';
import { Menu } from 'primereact/menu';
import Rater from 'react-rater';
import Button from '@Common/Button';
import MultiSelectBox from '@Common/MultiSelectBoxV6/MultiSelectBoxV6';
import Notification from '@Common/Notification';
import * as types from '@Constants/actionTypes';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import { LocationTaskLabel, LocationTaskTranslation, LocationTaskDetails, LocationTaskHistory, Step, TaskTag, DrawerFilters } from './Types';
import { Option } from '../../../../types/common';
import { buildOutlookCalendarUrl, buildGoogleCalendarURL, buildMailToUrl, buildOutlookCalendarICSFile } from '@Helpers/sharing';
import Comments from './Comments';
import Translate from './Translate';
import LinksAndFiles from './LinksAndFiles';
import Data from './Data';
import HistoryRow from './HistoryRow';
import TaskMessages from './TaskMessages';
import Steps from './Steps';
import './styles.scss';
import StatusSelector from '@Components/Common/StatusSelector/StatusSelector';
import FeedbackModal from './FeedbackModal';
import type { Modal61Ref } from '@Types/common';

const SHARE_MENU_ITEMS = {
    AddToOutlook: {
        displayName: 'Add to Outlook Calendar',
        id: 'outlook',
    },
    AddToOutlookcom: {
        displayName: 'Add to Outlook.com Calendar',
        id: 'outlookcom',
    },
    AddToGoogle: {
        displayName: 'Add to Google Calendar',
        id: 'google',
    },
    MailTo: {
        displayName: 'Copy Task Info to Email',
        id: 'mailto',
    },
    ShareLink: {
        displayName: 'Share Link',
        id: 'shareLink',
    },
};

interface LocationTaskSidebarProps {
    isDrawerVisible: boolean;
    closeDrawer: () => void;
    changeTask: (taskId: number) => void;
    onComplete: () => void;
    locationTaskId: number;
    locationKey: string;
    responsibleUsers: Option[];
    isMobile: boolean;
    onEditTask?: () => void;
}

const LocationTaskSidebar = (props: LocationTaskSidebarProps): ReactElement => {
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);
    const [isProjectCertified, setIsProjectCertified] = useState(false);
    const [locationTaskDetails, setLocationTaskDetails] = useState<LocationTaskDetails | null>(null);
    const [locationTaskHistory, setLocationTaskHistory] = useState<LocationTaskHistory[]>([]);
    const [drawerFilters, setDrawerFilters] = useState<DrawerFilters>(
        {
            isStartDateActive: false,
            isTaskStepsActive: false,
            isProjectWidgetActive: false,
            isProjectIdRequiredForLocationTask: false,
            isTaskDependenciesActive: false,
            allowLocationTaskDateChanges: false,
            defaultNotStartedStatusId: 0,
            completeStatusId: 0,
            locationOpeningDate: '',
            statuses: [],
            responsibleUsers: [],
            departments: [],
            communities: [],
            projects: [],
            phases: [],
            contingentTasks: [],
            taskTags: [],
        }
    );

    const [modifyResponsibleUser, setModifyResponsibleUser] = useState<boolean>(false);
    const [responsibleUser, setResponsibleUser] = useState<Option | null>(null);
    const [isEditingStartDate, setIsEditingStartDate] = useState<boolean>(false);
    const [isEditingTargetDate, setIsEditingTargetDate] = useState<boolean>(false);
    const [translation, setTranslation] = useState<LocationTaskTranslation | null>(null);
    const [translationLabels, setTranslationLabels] = useState<LocationTaskLabel>({
        language: '',
        translateLabel: 'Translate',
        translationRatingLabel: 0,
    });
    const [isDataAndHistoryVisible, setIsDataAndHistoryVisible] = useState<boolean>(false);
    const [isAddTagVisible, setIsAddTagVisible] = useState<boolean>(false);
    const [startDateInputValue, setStartDateInputValue] = useState<string>('');
    const [targetDateInputValue, setTargetDateInputValue] = useState<string>('');
    const [isTypingStartDate, setIsTypingStartDate] = useState<boolean>(false);
    const [isTypingTargetDate, setIsTypingTargetDate] = useState<boolean>(false);

    const feedbackModalRef: RefObject<Modal61Ref | null> = useRef<Modal61Ref | null>(null);
    const [selectedFeedbackOption, setSelectedFeedbackOption] = useState<string>('');
    const [feedbackText, setFeedbackText] = useState<string>('');

    const dispatch: Dispatch = useDispatch();
    const calendarMenuOverlayPanel = useRef<Menu>(null);
    const currentTaskIdRef = useRef<number>(props.locationTaskId);

    const getLocationTask = async (locationTaskId: number) => dispatch<any>(locationTaskActions.loadLocationTask(locationTaskId));
    const getHistory = async (locationTaskId: number) => dispatch<any>(locationTaskActions.loadHistory(locationTaskId));
    const getLocationTaskFilters = async (locationTaskId: number, locationKey: string) => dispatch<any>(locationTaskActions.loadLocationTaskDrawerFilters(locationTaskId, locationKey));
    const updateLocationTask = async (request: any) => dispatch<any>(locationTaskActions.saveLocationTaskEditMode(request));
    const rateTranslation = async (request) => dispatch<any>(locationTaskActions.translationRating(request));

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            try {
                await Promise.all([
                    loadLocationTask(props.locationTaskId),
                    loadLocationTaskFilters(props.locationTaskId, props.locationKey),
                ]);
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            } finally {
                setIsLoading(false);
            }
        };

        setResponsibleUser(null);
        setLocationTaskDetails(null);
        setModifyResponsibleUser(false);
        setIsEditingStartDate(false);
        setIsEditingTargetDate(false);
        setStartDateInputValue('');
        setTargetDateInputValue('');

        // Only load if we have a valid task ID (not in add mode)
        if (isVisible && props.locationTaskId && props.locationTaskId > 0) {
            fetchData();
        }
    }, [isVisible, props.locationTaskId, props.locationKey]);

    useEffect(() => {
        if (isDataAndHistoryVisible && props.locationTaskId > 0 && props.isDrawerVisible) {
            const fetchHistory = async () => {
                setIsHistoryLoading(true);
                setLocationTaskHistory([]);
                const taskIdForThisRequest = props.locationTaskId;
                currentTaskIdRef.current = taskIdForThisRequest;

                try {
                    const response = await getHistory(taskIdForThisRequest);

                    // Only apply results if this is still the current task
                    if (currentTaskIdRef.current === taskIdForThisRequest) {
                        if (response.type === types.LOAD_LOCATION_TASK_HISTORY_SUCCESS) {
                            setLocationTaskHistory(response.payload.histories);
                        } else if (response.type === types.LOAD_LOCATION_TASK_HISTORY_FAILURE) {
                            Notification("error", "Error", response.payload.message);
                            setIsDataAndHistoryVisible(false);
                        }
                    }
                } catch (error) {
                    // Only show error if this is still the current task
                    if (currentTaskIdRef.current === taskIdForThisRequest) {
                        Notification('error', 'Error', error);
                        setIsDataAndHistoryVisible(false);
                    }
                } finally {
                    // Only update loading state if this is still the current task
                    if (currentTaskIdRef.current === taskIdForThisRequest) {
                        setIsHistoryLoading(false);
                    }
                }
            };
            fetchHistory();
        }
    }, [isDataAndHistoryVisible, props.locationTaskId, props.isDrawerVisible]);

    useEffect(() => {
        if (locationTaskDetails?.locationTask) {
            if (locationTaskDetails.locationTask.startDate) {
                // Ensure we treat the date as local rather than UTC to avoid timezone shifts
                const localStartDate = moment(locationTaskDetails.locationTask.startDate).local().format('YYYY-MM-DD');
                setStartDateInputValue(localStartDate);
            }
            if (locationTaskDetails.locationTask.targetDate) {
                // Ensure we treat the date as local rather than UTC to avoid timezone shifts
                const localTargetDate = moment(locationTaskDetails.locationTask.targetDate).local().format('YYYY-MM-DD');
                setTargetDateInputValue(localTargetDate);
            }
        }
    }, [locationTaskDetails?.locationTask?.startDate, locationTaskDetails?.locationTask?.targetDate]);

    useEffect(() => {
        if (locationTaskDetails?.locationTask?.responsibleUserId && props.responsibleUsers.length > 0) {
            const user = props.responsibleUsers.find(u => u.id === locationTaskDetails.locationTask.responsibleUserId);
            setResponsibleUser(user || null);
        } else {
            setResponsibleUser(null);
        }
    }, [locationTaskDetails?.locationTask?.responsibleUserId, props.responsibleUsers]);

    const loadLocationTask = async (locationTaskId: number) => {
        if (locationTaskId <= 0) return;
        setIsLoading(true);
        try {
            const response = await getLocationTask(locationTaskId);

            if (response.type === types.LOAD_LOCATION_TASK_SUCCESS) {
                const { payload } = response;
                setLocationTaskDetails(payload as LocationTaskDetails);
                setIsProjectCertified(payload.isProjectCertified || false);
            }
            else if (response.type === types.LOAD_LOCATION_TASK_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadLocationTaskFilters = async (locationTaskId: number, locationKey: string) => {
        if (locationTaskId <= 0) return;
        setIsLoading(true);
        try {
            const response = await getLocationTaskFilters(locationTaskId, locationKey);

            if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_SUCCESS) {
                setDrawerFilters(response.payload as DrawerFilters);
            } else if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const saveLocationTaskChanges = async () => {
        if (!locationTaskDetails?.locationTask) return;

        setIsLoading(true);

        const req = {
            ...locationTaskDetails.locationTask,
            locationTaskId: locationTaskDetails.locationTask.id,
            statusId: locationTaskDetails.locationTask.statusId,
            responsibleUserId: locationTaskDetails.locationTask.responsibleUserId,
            startDate: locationTaskDetails.locationTask.startDate,
            targetDate: locationTaskDetails.locationTask.targetDate,
            isProjectCertified: locationTaskDetails.locationTask.isProjectCertified || false,
            projectCoachId: locationTaskDetails.projectCoachId,
            steps: locationTaskDetails.steps.filter(step => step.name?.trim().length > 0),
            FinancialsBudget: null,
            FinancialsActual: null,
            tagIds: locationTaskDetails.locationTaskTagIds || [],
        }

        try {
            const response = await updateLocationTask(req);

            if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_SUCCESS) {
                Notification('success', 'Task Updated', 'Task has been updated');
                props.onComplete();
                props.closeDrawer();
            } else if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onResponsibleUserChange = (value, name) => {
        const userId = value === '' || value == null ? 0 : parseInt(value, 10);
        setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                locationTask: {
                    ...prev.locationTask,
                    responsibleUserId: userId,
                },
            };
        });
        setModifyResponsibleUser(false);
    };

    const onStatusChange = (value) => {
        setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                locationTask: {
                    ...prev.locationTask,
                    statusId: value,
                },
            };
        });
    };

    const startDateCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const targetDateCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (startDateCommitTimeoutRef.current) {
                clearTimeout(startDateCommitTimeoutRef.current);
                startDateCommitTimeoutRef.current = null;
            }
            if (targetDateCommitTimeoutRef.current) {
                clearTimeout(targetDateCommitTimeoutRef.current);
                targetDateCommitTimeoutRef.current = null;
            }
        };
    }, []);

    // Helper functions for date validation
    const isValidCompleteDate = (date: string): boolean => {
        return date.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(date) && moment(date, 'YYYY-MM-DD', true).isValid();
    };

    const isValidDateInput = (date: string): boolean => {
        return moment(date, 'YYYY-MM-DD', true).isValid();
    };

    const onStartDateInputChange = (date: string) => {
        setStartDateInputValue(date);

        if (startDateCommitTimeoutRef.current) {
            clearTimeout(startDateCommitTimeoutRef.current);
            startDateCommitTimeoutRef.current = null;
        }

        // If not actively typing (calendar selection), commit immediately
        if (!isTypingStartDate && isValidCompleteDate(date)) {
            startDateCommitTimeoutRef.current = setTimeout(() => {
                onStartDateCommit(date);
            }, 100); // Small delay to ensure calendar closed
        }
    };

    const onStartDateCommit = (date: string) => {
        if (startDateCommitTimeoutRef.current) {
            clearTimeout(startDateCommitTimeoutRef.current);
            startDateCommitTimeoutRef.current = null;
        }

        // Validate the date input
        if (!isValidDateInput(date)) {
            Notification('error', 'Invalid Date', 'Please enter a valid date.');
            return;
        }

        setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                locationTask: {
                    ...prev.locationTask,
                    startDate: moment(date, 'YYYY-MM-DD').toDate(),
                },
            };
        });
        setIsEditingStartDate(false);
    };

    const onTargetDateInputChange = (date: string) => {
        setTargetDateInputValue(date);

        if (targetDateCommitTimeoutRef.current) {
            clearTimeout(targetDateCommitTimeoutRef.current);
            targetDateCommitTimeoutRef.current = null;
        }

        // If not actively typing (calendar selection), commit immediately
        if (!isTypingTargetDate && isValidCompleteDate(date)) {
            targetDateCommitTimeoutRef.current = setTimeout(() => {
                onTargetDateCommit(date);
            }, 100); // Small delay to ensure calendar closed
        }
    };

    const onTargetDateCommit = (date: string) => {
        if (targetDateCommitTimeoutRef.current) {
            clearTimeout(targetDateCommitTimeoutRef.current);
            targetDateCommitTimeoutRef.current = null;
        }

        // Validate the date input
        if (!isValidDateInput(date)) {
            Notification('error', 'Invalid Date', 'Please enter a valid date.');
            return;
        }

        // Validate target date against successor tasks
        const newTargetDate = moment(date, 'YYYY-MM-DD').toDate();
        const currentTaskId = locationTaskDetails?.locationTask?.id;

        if (currentTaskId && locationTaskDetails.successorTasks?.length > 0) {
            // Find successor tasks that depend on this task
            const successorTasks = locationTaskDetails.successorTasks;

            // Check if new target date conflicts with any successor
            const conflictingSuccessor = successorTasks.find(successor => {
                if (!successor.targetDate) return false;
                return newTargetDate > new Date(successor.targetDate);
            });

            if (conflictingSuccessor) {
                const conflictTaskName = conflictingSuccessor.name || 'Unnamed Task';
                const successorDate = moment(conflictingSuccessor.targetDate).format('MMM D, YYYY');
                const requestedDate = moment(newTargetDate).format('MMM D, YYYY');

                Notification(
                    'error',
                    'Invalid Target Date',
                    `Cannot set target date to ${requestedDate}. This task has a dependent task "${conflictTaskName}" with a target date of ${successorDate}. Please choose a date on or before ${successorDate} to maintain proper task sequencing.`
                );
                return; // Prevent the date change
            }
        }

        setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                locationTask: {
                    ...prev.locationTask,
                    targetDate: newTargetDate,
                },
            };
        });
        setIsEditingTargetDate(false);
    };

    const setStartDateToToday = () => {
        const today = moment().format('YYYY-MM-DD');
        setStartDateInputValue(today);
        onStartDateCommit(today);
    };

    const setTargetDateToToday = () => {
        const today = moment().format('YYYY-MM-DD');
        setTargetDateInputValue(today);
        onTargetDateCommit(today);
    };

    const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, isStartDate: boolean) => {
        // Mark as actively typing when any key is pressed
        if (isStartDate) {
            setIsTypingStartDate(true);
        } else {
            setIsTypingTargetDate(true);
        }

        if (e.key === 'Escape') {
            if (isStartDate) {
                setIsEditingStartDate(false);
                setStartDateInputValue(
                    moment(locationTaskDetails?.locationTask?.startDate).isValid()
                        ? moment(locationTaskDetails?.locationTask?.startDate).local().format('YYYY-MM-DD')
                        : ''
                );
                setIsTypingStartDate(false);
            } else {
                setIsEditingTargetDate(false);
                setTargetDateInputValue(
                    moment(locationTaskDetails?.locationTask?.targetDate).isValid()
                        ? moment(locationTaskDetails?.locationTask?.targetDate).local().format('YYYY-MM-DD')
                        : ''
                );
                setIsTypingTargetDate(false);
            }
        } else if (e.key === 'Enter') {
            const inputValue = (e.target as HTMLInputElement).value;
            if (isStartDate) {
                setIsTypingStartDate(false);
                onStartDateCommit(inputValue);
            } else {
                setIsTypingTargetDate(false);
                onTargetDateCommit(inputValue);
            }
        }
    };

    const onCertificationChange = (certified: boolean) => {
        setIsProjectCertified(certified)
        setLocationTaskDetails((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                locationTask: {
                    ...prev.locationTask,
                    isProjectCertified: certified,
                },
            };
        });
    };

    const modifyResponsibleUserContent = () => {
        return (
            <MultiSelectBox
                className='custom-multiselect w-100'
                name='responsibleUserId'
                idField='id'
                valueField='name'
                options={props.responsibleUsers}
                onChange={onResponsibleUserChange}
                selectedValues={responsibleUser?.id.toString() ?? ''}
                isMulti={false}
                isSearchable={false}
            />
            // <Dropdown value={responsibleUser?.id} options={props.responsibleUsers} optionValue='id' optionLabel='name' onChange={onResponsibleUserChange} />
        );
    };

    const menuOptionClick = (eventItemId?: string) => {
        if (!locationTaskDetails?.locationTask) return;

        const locationTask = locationTaskDetails.locationTask;

        // dates need to be in a certain format. User the start date if there is one, otherwise use target date
        const eventDate: Date = locationTask.startDate ? locationTask.startDate : locationTask.targetDate;
        const eventDateString = moment.utc(eventDate).format('YYYYMMDDTHHmmssZ').replace('+00:00', 'Z');

        // get the responsible user name if there is one
        let responsibleUserName = '';
        if (locationTask.responsibleUserId) {
            const responsibleUser = props.responsibleUsers.find((m) => m.id === locationTask.responsibleUserId);
            if (responsibleUser) {
                responsibleUserName = responsibleUser.name;
            }
        }

        if (eventItemId === SHARE_MENU_ITEMS.AddToOutlook.id) {
            const url = buildOutlookCalendarICSFile(locationTask, eventDateString, responsibleUserName);
            const filename = 'download.ics';
            const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });
            /****************************************************************
            // many browsers do not properly support downloading data URIs
            // (even with "download" attribute in use) so this solution
            // ensures the event will download cross-browser
            ****************************************************************/
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (eventItemId === SHARE_MENU_ITEMS.AddToOutlookcom.id) {
            window.open(buildOutlookCalendarUrl(locationTask, eventDateString, responsibleUserName), '_blank');
        } else if (eventItemId === SHARE_MENU_ITEMS.AddToGoogle.id) {
            window.open(buildGoogleCalendarURL(locationTask, eventDateString, responsibleUserName), '_blank');
        } else if (eventItemId === SHARE_MENU_ITEMS.MailTo.id) {
            window.open(buildMailToUrl(locationTask, responsibleUserName), '_self');
        } else if (eventItemId === SHARE_MENU_ITEMS.ShareLink.id) {
            // copy to clipboard and show toastr message
            const linkToCopy = window.location.href;
            navigator.clipboard
                .writeText(linkToCopy)
                .then(() => {
                    Notification('success', '', 'A link to this task has been copied to your clipboard');
                })
                .catch(() => {
                    Notification('error', '', 'Failed to copy the link to your clipboard');
                });
        }
    };

    const calendarMenuOptions = () => {
        const menuOptions = [
            {
                label: SHARE_MENU_ITEMS.AddToOutlook.displayName,
                id: SHARE_MENU_ITEMS.AddToOutlook.id,
                command: () => menuOptionClick(SHARE_MENU_ITEMS.AddToOutlook.id),
            },
            {
                label: SHARE_MENU_ITEMS.AddToOutlookcom.displayName,
                id: SHARE_MENU_ITEMS.AddToOutlookcom.id,
                command: () => menuOptionClick(SHARE_MENU_ITEMS.AddToOutlookcom.id),
            },
            {
                label: SHARE_MENU_ITEMS.AddToGoogle.displayName,
                id: SHARE_MENU_ITEMS.AddToGoogle.id,
                command: () => menuOptionClick(SHARE_MENU_ITEMS.AddToGoogle.id),
            },
        ];

        return menuOptions;
    };

    const handleEmailClick = () => {
        menuOptionClick(SHARE_MENU_ITEMS.MailTo.id);
    };

    const handleGetLinkClick = () => {
        menuOptionClick(SHARE_MENU_ITEMS.ShareLink.id);
    };

    const onRateTranslation = async (translationId, rating) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setTranslation((prev) => ({ ...prev!, rating: rating }));

        try {
            const request = { translationId, rating };
            await rateTranslation(request);
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const handleTaskClick = (taskId: number) => {
        props.changeTask(taskId)
    };

    const handleStepsUpdate = (steps: Step[]) => {
        setLocationTaskDetails(prev => {
            if (!prev) return prev;
            return { ...prev, steps };
        });
    };

    const handleStepUpdate = (step: Step) => {
        setLocationTaskDetails(prev => {
            if (!prev) return prev;
            const updatedSteps = prev.steps.map(s => s.id === step.id ? step : s);
            return { ...prev, steps: updatedSteps };
        });
    };

    const onRemoveLocationTag = (tagId: number) => {
        setLocationTaskDetails(prev => {
            if (!prev) return prev;
            const updatedTagIds = prev.locationTaskTagIds.filter(id => id !== tagId);
            return { ...prev, locationTaskTagIds: updatedTagIds };
        });
    };

    const onAddLocationTag = (tagId: number) => {
        setLocationTaskDetails(prev => {
            if (!prev) return prev;
            if (prev.locationTaskTagIds.includes(tagId)) {
                return prev; // Already has this tag
            }
            const updatedTagIds = [...prev.locationTaskTagIds, tagId];
            return { ...prev, locationTaskTagIds: updatedTagIds };
        });
        setIsAddTagVisible(false);
    };

    const handleOpenFeedbackModal = () => {
        setSelectedFeedbackOption('');
        setFeedbackText('');
        feedbackModalRef.current?.show();
    };

    const handleCloseFeedbackModal = () => {
        feedbackModalRef.current?.hide();
        setSelectedFeedbackOption('');
        setFeedbackText('');
    };


    const onShowSidebar = () => {
        setIsVisible(true);
    }

    const onHideSidebar = () => {
        setIsVisible(false);
        props.closeDrawer();
    }

    const onShowHideDataAndHistory = () => {
        setIsDataAndHistoryVisible((prevVisible) => {
            const nextVisible = !prevVisible;

            if (nextVisible) {
                setIsHistoryLoading(true);
                setLocationTaskHistory([]);
            }

            return nextVisible;
        });
    };

    const sidebarContent = () => {
        const locationTask = locationTaskDetails?.locationTask;

        // Check if task is past due
        const isPastDue = locationTask?.targetDate && moment(locationTask.targetDate).isBefore(moment(), 'day');

        return isVisible && <>
            <Menu className='mt-1' model={calendarMenuOptions()} popup ref={calendarMenuOverlayPanel} />

            {/* Improved Header */}
            <div className="card-header" style={{
                backgroundColor: '#f6f9fc',
                padding: '8px 24px',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #dee2e6'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        {locationTask?.isMilestone && (
                            <>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ color: '#6860e4' }}>
                                    <path d="M9 2L11.5 7L17 8L13 12L14 17L9 14.5L4 17L5 12L1 8L6.5 7L9 2Z" fill="currentColor" />
                                </svg>
                                <span style={{
                                    fontFamily: 'Greycliff',
                                    fontWeight: '700',
                                    color: '#343a40',
                                    fontSize: '16px',
                                    lineHeight: '24px'
                                }}>Milestone</span>
                            </>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        {locationTaskDetails?.locationTask && <div>
                            <StatusSelector
                                isPastDue={isPastDue}
                                statuses={drawerFilters.statuses}
                                onChange={onStatusChange}
                                selectedStatusId={locationTaskDetails.locationTask.statusId ?? 0}
                            />
                        </div>}
                        <button
                            onClick={props.closeDrawer}
                            style={{
                                padding: '8px',
                                background: 'none',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dee2e6'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: '#6c757d' }}>
                                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <Skeleton width='100%' height='100%' className='h-100 m-3' />
            ) :
                <div className='location-task-sidebar vh-100'>
                    {/* Main Content */}
                    <div style={{ padding: '0' }}>
                        {/* Dates and Assignment Grid */}
                        <div className='dates-assignment-grid' style={{
                            display: 'grid',
                            gap: '16px',
                            marginBottom: '12px'
                        }}>
                            {/* Dates Box */}
                            <div style={{
                                backgroundColor: '#fffdf0',
                                padding: '20px',
                                borderRadius: '8px'
                            }}>
                                <div className={`date-grid${locationTask?.startDate && locationTask?.targetDate ? ' two-columns' : ''}`} style={{
                                    display: 'grid',
                                    gridTemplateColumns: locationTask?.startDate && locationTask?.targetDate ? '1fr 1fr' : '1fr',
                                    gap: '16px'
                                }}>
                                    {/* Start Date */}
                                    {
                                        locationTask?.startDate &&
                                        (
                                            <div className='group' style={{ position: 'relative' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: '#6c757d' }}>
                                                        <rect x="2" y="3" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                                                        <path d="M5 1.5V4.5M10 1.5V4.5M2 6.5H13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                                    </svg>
                                                    <label style={{
                                                        fontFamily: 'Greycliff',
                                                        color: '#6c757d',
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        fontWeight: '400',
                                                        letterSpacing: '0.02em'
                                                    }}>Start By</label>
                                                </div>
                                                <div>
                                                    {isEditingStartDate ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <input
                                                                    type="date"
                                                                    value={startDateInputValue}
                                                                    onChange={(e) => onStartDateInputChange(e.target.value)}
                                                                    onKeyDown={(e) => handleDateInputKeyDown(e, true)}
                                                                    onFocus={() => setIsTypingStartDate(false)} // Reset typing state on focus
                                                                    onBlur={() => {
                                                                        const wasTyping = isTypingStartDate;
                                                                        setIsTypingStartDate(false);
                                                                        // Only commit on blur if we have a valid complete date and were typing
                                                                        if (wasTyping && startDateInputValue && startDateInputValue.length === 10) {
                                                                            onStartDateCommit(startDateInputValue);
                                                                        }
                                                                    }}
                                                                    autoFocus
                                                                    style={{
                                                                        fontFamily: 'Greycliff',
                                                                        fontSize: '14px',
                                                                        padding: '4px 8px',
                                                                        border: '1px solid #5341c6',
                                                                        borderRadius: '4px',
                                                                        outline: 'none'
                                                                    }}
                                                                />
                                                                <button
                                                                    onClick={setStartDateToToday}
                                                                    style={{
                                                                        fontFamily: 'Greycliff',
                                                                        color: '#5341c6',
                                                                        fontSize: '12px',
                                                                        background: 'none',
                                                                        border: '1px solid #5341c6',
                                                                        borderRadius: '4px',
                                                                        padding: '4px 8px',
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >Today</button>
                                                                <button
                                                                    onClick={() => setIsEditingStartDate(false)}
                                                                    style={{
                                                                        fontFamily: 'Greycliff',
                                                                        color: '#6c757d',
                                                                        fontSize: '12px',
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <p style={{
                                                                fontFamily: 'Greycliff',
                                                                fontWeight: '500',
                                                                color: '#31363a',
                                                                fontSize: '14px',
                                                                lineHeight: '20px',
                                                                margin: 0
                                                            }}>
                                                                {moment(locationTask?.startDate).format('D MMM YYYY')}
                                                            </p>
                                                            {drawerFilters.allowLocationTaskDateChanges && <button
                                                                onClick={() => {
                                                                    setStartDateInputValue(moment(locationTask.startDate).format('YYYY-MM-DD'));
                                                                    setIsEditingStartDate(true);
                                                                }}
                                                                disabled={!drawerFilters.allowLocationTaskDateChanges}
                                                                style={{
                                                                    fontFamily: 'Greycliff',
                                                                    color: drawerFilters.allowLocationTaskDateChanges ? '#5341c6' : '#6c757d',
                                                                    fontSize: '12px',
                                                                    lineHeight: '16px',
                                                                    fontWeight: '400',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: drawerFilters.allowLocationTaskDateChanges ? 'pointer' : 'not-allowed',
                                                                    textDecoration: 'none',
                                                                    opacity: 0,
                                                                    transition: 'opacity 0.2s ease'
                                                                }}
                                                                className='edit-btn'
                                                            >Edit</button>}
                                                        </div>
                                                    )}
                                                    <span style={{
                                                        fontFamily: 'Greycliff',
                                                        color: '#6c757d',
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        fontWeight: '400'
                                                    }}>
                                                        {moment(locationTask?.startDate).fromNow()}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                    {/* Target Date */}
                                    {locationTask?.targetDate && (
                                        <div className='group' style={{ position: 'relative', gridColumn: !locationTask?.startDate ? '1 / -1' : 'auto' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: isPastDue ? '#dc3545' : '#6c757d' }}>
                                                    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="1.3" />
                                                    <circle cx="7.5" cy="7.5" r="2.2" fill="currentColor" />
                                                    <path d="M7.5 2V0.5M7.5 14.5V13M13 7.5H14.5M1 7.5H2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                                </svg>
                                                <label style={{
                                                    fontFamily: 'Greycliff',
                                                    color: isPastDue ? '#dc3545' : '#6c757d',
                                                    fontSize: '12px',
                                                    lineHeight: '16px',
                                                    fontWeight: '400',
                                                    letterSpacing: '0.02em'
                                                }}>Target Date</label>
                                                {isPastDue && (
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: '#dc3545' }}>
                                                        <path d="M7 1L13 13H1L7 1Z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M7 5V8M7 10V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div>
                                                {isEditingTargetDate ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <input
                                                                type="date"
                                                                value={targetDateInputValue}
                                                                onChange={(e) => onTargetDateInputChange(e.target.value)}
                                                                onKeyDown={(e) => handleDateInputKeyDown(e, false)}
                                                                onFocus={() => setIsTypingTargetDate(false)} // Reset typing state on focus
                                                                onBlur={() => {
                                                                    const wasTyping = isTypingTargetDate;
                                                                    setIsTypingTargetDate(false);
                                                                    // Only commit on blur if we have a valid complete date and were typing
                                                                    if (wasTyping && targetDateInputValue && targetDateInputValue.length === 10) {
                                                                        onTargetDateCommit(targetDateInputValue);
                                                                    }
                                                                }}
                                                                autoFocus
                                                                style={{
                                                                    fontFamily: 'Greycliff',
                                                                    fontSize: '14px',
                                                                    padding: '4px 8px',
                                                                    border: '1px solid #5341c6',
                                                                    borderRadius: '4px',
                                                                    outline: 'none'
                                                                }}
                                                            />
                                                            <button
                                                                onClick={setTargetDateToToday}
                                                                style={{
                                                                    fontFamily: 'Greycliff',
                                                                    color: '#5341c6',
                                                                    fontSize: '12px',
                                                                    background: 'none',
                                                                    border: '1px solid #5341c6',
                                                                    borderRadius: '4px',
                                                                    padding: '4px 8px',
                                                                    cursor: 'pointer',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >Today</button>
                                                            <button
                                                                onClick={() => setIsEditingTargetDate(false)}
                                                                style={{
                                                                    fontFamily: 'Greycliff',
                                                                    color: '#6c757d',
                                                                    fontSize: '12px',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <p style={{
                                                            fontFamily: 'Greycliff',
                                                            fontWeight: '500',
                                                            color: isPastDue ? '#dc3545' : '#31363a',
                                                            fontSize: '14px',
                                                            lineHeight: '20px',
                                                            margin: 0
                                                        }}>
                                                            {moment(locationTask.targetDate).format('D MMM YYYY')}
                                                        </p>
                                                        {drawerFilters.allowLocationTaskDateChanges && <button
                                                            onClick={() => {
                                                                setTargetDateInputValue(moment(locationTask.targetDate).format('YYYY-MM-DD'));
                                                                setIsEditingTargetDate(true);
                                                            }}
                                                            disabled={!drawerFilters.allowLocationTaskDateChanges}
                                                            style={{
                                                                fontFamily: 'Greycliff',
                                                                color: drawerFilters.allowLocationTaskDateChanges ? '#5341c6' : '#6c757d',
                                                                fontSize: '12px',
                                                                lineHeight: '16px',
                                                                fontWeight: '400',
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: drawerFilters.allowLocationTaskDateChanges ? 'pointer' : 'not-allowed',
                                                                textDecoration: 'none',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s ease'
                                                            }}
                                                            className='edit-btn'
                                                        >Edit</button>}
                                                    </div>
                                                )}
                                                <span style={{
                                                    fontFamily: 'Greycliff',
                                                    color: isPastDue ? '#dc3545' : '#6c757d',
                                                    fontSize: '12px',
                                                    lineHeight: '16px',
                                                    fontWeight: '400'
                                                }}>
                                                    {isPastDue
                                                        ? `${Math.abs(moment(locationTask.targetDate).diff(moment(), 'days'))} days overdue`
                                                        : moment(locationTask.targetDate).fromNow()
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Assignment Box */}
                            <div style={{
                                borderRadius: '8px',
                                backgroundColor: '#fffdf0',
                                padding: '20px'
                            }}>
                                <div className='group' style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: '#6c757d' }}>
                                            <circle cx="7.5" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                                            <path d="M2.5 13C2.5 10.5 4.5 9 7.5 9C10.5 9 12.5 10.5 12.5 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                        </svg>
                                        <label style={{
                                            fontFamily: 'Greycliff',
                                            color: '#6c757d',
                                            fontSize: '12px',
                                            lineHeight: '16px',
                                            fontWeight: '400',
                                            letterSpacing: '0.02em'
                                        }}>Assigned To</label>
                                    </div>

                                    {modifyResponsibleUser ? (
                                        modifyResponsibleUserContent()
                                    ) : responsibleUser ? (
                                        // Assigned State
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                                <Avatar
                                                    shape='circle'
                                                    size='normal'
                                                    label={responsibleUser.name
                                                        ?.split(' ')
                                                        .map((word) => word.charAt(0))
                                                        .join('')}
                                                    style={{ width: '36px', height: '36px' }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '-2px',
                                                    right: '-2px',
                                                    width: '14px',
                                                    height: '14px',
                                                    backgroundColor: '#1ea97c',
                                                    border: '2px solid white',
                                                    borderRadius: '50%'
                                                }} title="Online" />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <p style={{
                                                        fontFamily: 'Greycliff',
                                                        fontWeight: '500',
                                                        color: '#31363a',
                                                        fontSize: '14px',
                                                        lineHeight: '20px',
                                                        margin: 0
                                                    }}>
                                                        {responsibleUser.name}
                                                    </p>
                                                    <button
                                                        onClick={() => setModifyResponsibleUser(true)}
                                                        style={{
                                                            fontFamily: 'Greycliff',
                                                            color: '#5341c6',
                                                            fontSize: '12px',
                                                            lineHeight: '16px',
                                                            fontWeight: '400',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            textDecoration: 'none',
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }}
                                                        className='edit-btn'
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                                <p style={{
                                                    fontFamily: 'Greycliff',
                                                    color: '#6c757d',
                                                    fontSize: '12px',
                                                    lineHeight: '16px',
                                                    fontWeight: '400',
                                                    margin: '2px 0 0 0'
                                                }}>
                                                    {locationTaskDetails?.jobTitleName}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        // Unassigned State
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <p style={{
                                                        fontFamily: 'Greycliff',
                                                        fontWeight: '500',
                                                        color: '#6c757d',
                                                        fontSize: '14px',
                                                        lineHeight: '20px',
                                                        margin: 0
                                                    }}>
                                                        {locationTaskDetails?.jobTitleName || 'Assign Task'}
                                                    </p>
                                                    <p style={{
                                                        fontFamily: 'Greycliff',
                                                        color: '#6c757d',
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        fontWeight: '400',
                                                        margin: '2px 0 0 0'
                                                    }}>Suggested</p>
                                                </div>
                                                <button
                                                    onClick={() => setModifyResponsibleUser(true)}
                                                    style={{
                                                        fontFamily: 'Greycliff',
                                                        color: '#5341c6',
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        fontWeight: '600',
                                                        padding: '8px 14px',
                                                        backgroundColor: 'white',
                                                        border: '1px solid #5341c6',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        flexShrink: 0
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#5341c6';
                                                        e.currentTarget.style.color = 'white';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.color = '#5341c6';
                                                    }}
                                                >
                                                    Assign
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Task Messages */}
                        <div className='row g-3'>
                            <div className='col-12 mb-3'>
                                <TaskMessages
                                    locationTaskDetails={locationTaskDetails}
                                    statuses={drawerFilters.statuses}
                                    onTaskClick={handleTaskClick}
                                    isProjectCertified={isProjectCertified}
                                    onCertificationChange={onCertificationChange}
                                />
                            </div>
                        </div>
                        {/* Task Messages */}

                        {/* Main Content - Two Column Layout: Task Details (Left, Wider) and Comments (Right, Narrower) */}
                        <div className='row g-3'>
                            {/* Left Column - Task Details and Steps (Wider) */}
                            <div className='col-12 task-details-column'>
                                {/* Task Description Card */}
                                <div className='card mb-3'>
                                    <div className='card-body'>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            position: 'relative',
                                            width: '100%'
                                        }}>
                                            <h3 style={{
                                                fontFamily: 'Greycliff',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                lineHeight: '20px',
                                                color: '#212529',
                                                width: '100%',
                                                margin: 0
                                            }}>
                                                {locationTask?.name}
                                            </h3>
                                            <p style={{
                                                fontFamily: 'Greycliff',
                                                fontSize: '14px',
                                                fontWeight: '400',
                                                lineHeight: '20px',
                                                color: '#495057',
                                                width: '100%',
                                                margin: 0
                                            }}>
                                                {locationTask?.additionalInfo}
                                            </p>

                                            {translation && (
                                                <>
                                                    <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                                                    <h3 style={{
                                                        fontFamily: 'Greycliff',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        lineHeight: '20px',
                                                        color: '#212529',
                                                        margin: 0
                                                    }}>
                                                        {translation.name}
                                                    </h3>
                                                    <p style={{
                                                        fontFamily: 'Greycliff',
                                                        fontSize: '14px',
                                                        fontWeight: '400',
                                                        lineHeight: '20px',
                                                        color: '#495057',
                                                        margin: 0
                                                    }}>
                                                        {translation.additionalInfo}
                                                    </p>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <Rater
                                                            total={5}
                                                            rating={translation.rating}
                                                            interactive={true}
                                                            onRate={(e) => onRateTranslation(translation.id, e.rating)}
                                                        />
                                                        <span style={{
                                                            fontFamily: 'Greycliff',
                                                            fontSize: '12px',
                                                            lineHeight: '16px',
                                                            fontWeight: '400',
                                                            color: '#6c757d'
                                                        }}>{translationLabels.translationRatingLabel}</span>
                                                    </div>
                                                </>
                                            )}

                                            {locationTask?.anticipatedEffort && (
                                                <p style={{
                                                    fontFamily: 'Greycliff',
                                                    fontSize: '14px',
                                                    fontWeight: '400',
                                                    lineHeight: '20px',
                                                    color: '#6c757d',
                                                    width: '100%',
                                                    margin: 0
                                                }}>
                                                    Anticipated Effort: {locationTask?.anticipatedEffort}
                                                </p>
                                            )}

                                            {/* Tags Section */}
                                            {((locationTaskDetails?.baselineTaskTags && locationTaskDetails.baselineTaskTags.length > 0) ||
                                                (locationTaskDetails?.locationTaskTagIds && locationTaskDetails.locationTaskTagIds.length > 0)) && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        width: '100%',
                                                        padding: '10px 0',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        {/* Location-level tags (removable) */}
                                                        {locationTaskDetails?.locationTaskTagIds?.map(tagId => {
                                                            const tag = drawerFilters.taskTags?.find(t => t.id === tagId);
                                                            if (!tag) return null;
                                                            return (
                                                                <span key={tagId} className='tag-pill' style={{
                                                                    backgroundColor: tag.styles?.backgroundColor || '#6c757d',
                                                                    color: tag.styles?.color || '#ffffff'
                                                                }}>
                                                                    {tag.name}
                                                                    <button
                                                                        onClick={() => onRemoveLocationTag(tagId)}
                                                                        className='tag-remove-button'
                                                                        style={{
                                                                            color: tag.styles?.color || '#ffffff'
                                                                        }}
                                                                        aria-label={`Remove tag ${tag.name}`}
                                                                    >
                                                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className='tag-remove-icon'>
                                                                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeLinecap="round" />
                                                                        </svg>
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}

                                                        {/* Baseline-level tags (not removable) */}
                                                        {locationTaskDetails?.baselineTaskTags?.map((tag) => (
                                                            <span key={tag.taskTagId} className='tag-pill' style={{
                                                                backgroundColor: tag.backgroundColor || '#6c757d',
                                                                color: tag.fontColor || '#ffffff'
                                                            }}>
                                                                {tag.title}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className='card-footer'>
                                        <div className='footer-actions'>
                                            {isAddTagVisible ? (
                                                <div className='tag-selector-container'>
                                                    <MultiSelectBox
                                                        name='locationTaskTagIds'
                                                        options={drawerFilters.taskTags
                                                            ?.filter(tag => !locationTaskDetails?.locationTaskTagIds?.includes(tag.id) && !locationTaskDetails?.baselineTaskTags?.some(bt => bt.taskTagId === tag.id))}
                                                        idField='id'
                                                        valueField='name'
                                                        selectedValues=""
                                                        onChange={(tagId) => onAddLocationTag(parseInt(tagId))}
                                                        isMulti={true}
                                                        label='Tags'
                                                    />
                                                    <button
                                                        type='button'
                                                        className='close-button'
                                                        onClick={() => setIsAddTagVisible(false)}
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    {!locationTask?.baselineTaskId && (
                                                        <button
                                                            type='button'
                                                            title='Edit Task'
                                                            className='action-button'
                                                            onClick={() => props.onEditTask?.()}
                                                        >
                                                            <i className="fa-thin fa-pencil action-icon"></i>
                                                            <span className='action-label'>Edit</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        type='button'
                                                        title='Add to Calendar'
                                                        className='action-button'
                                                        onClick={(e) => calendarMenuOverlayPanel.current && calendarMenuOverlayPanel.current.toggle(e)}
                                                    >
                                                        <i className="fa-thin fa-calendar action-icon"></i>
                                                        <span className='action-label'>Calendar</span>
                                                    </button>

                                                    <button
                                                        type='button'
                                                        title='Copy Task Info to Email'
                                                        className='action-button'
                                                        onClick={handleEmailClick}
                                                    >
                                                        <i className="fa-thin fa-envelope action-icon"></i>
                                                        <span className='action-label'>Email</span>
                                                    </button>

                                                    <button
                                                        type='button'
                                                        title='Get Share Link'
                                                        className='action-button'
                                                        onClick={handleGetLinkClick}
                                                    >
                                                        <i className="fa-thin fa-share-from-square action-icon"></i>
                                                        <span className='action-label'>Get Link</span>
                                                    </button>

                                                    <button
                                                        onClick={() => setIsAddTagVisible(true)}
                                                        title="Add Tag"
                                                        className='action-button'
                                                    >
                                                        <i className="fa-thin fa-tag action-icon"></i>
                                                        <span className='action-label'>Tag</span>
                                                    </button>

                                                    {locationTask?.baselineTaskId && (
                                                        <button
                                                            onClick={handleOpenFeedbackModal}
                                                            title="Suggestions"
                                                            className='action-button'
                                                            type='button'
                                                        >
                                                            <i className='fa-thin fa-lightbulb action-icon'></i>
                                                            <span className='action-label'>Suggestions</span>
                                                        </button>
                                                    )}

                                                    <Translate
                                                        newDesign
                                                        locationTaskId={props.locationTaskId}
                                                        translationLabels={translationLabels}
                                                        setTranslationLabels={setTranslationLabels}
                                                        translation={translation}
                                                        setTranslation={setTranslation}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Steps Card */}
                                {(drawerFilters.isTaskStepsActive) && (
                                    <div className='card my-3'>
                                        <Steps
                                            steps={locationTaskDetails?.steps || []}
                                            onStepsUpdate={handleStepsUpdate}
                                            onStepUpdate={handleStepUpdate}
                                            isReadOnly={false}
                                            locationTaskId={props.locationTaskId}
                                        />
                                    </div>
                                )}

                                {/* Links & Files Card */}
                                <LinksAndFiles
                                    locationTaskId={props.locationTaskId}
                                    links={locationTaskDetails?.links}
                                    files={locationTaskDetails?.files}
                                    isMobile={props.isMobile}
                                />
                            </div>

                            {/* Right Column - Comments (Narrower) */}
                            <div className='col-12 comments-column'>
                                <div className='card'>
                                    <div className='card-header'>
                                        <h3 className='card-title'>
                                            <i className='fas fa-comments me-2'></i>
                                            Comments
                                        </h3>
                                    </div>
                                    <div className='card-body p-0'>
                                        {/* increase width                                     */}
                                        <div style={{ padding: '16px' }} data-component="comments">
                                            <Comments locationTaskId={props.locationTaskId} locationKey={props.locationKey} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* End Main Content */}

                        {/* Data and History */}
                        <div className='row g-3'>
                            <div className='col-12'>

                                {isHistoryLoading ? (
                                    <Skeleton width='100%' className='h-100 m-3' />
                                ) :
                                    <div className='data-history'>
                                        <div>
                                            <span style={{
                                                fontFamily: 'Greycliff',
                                                fontWeight: '700',
                                                fontSize: '16px',
                                                lineHeight: '24px',
                                                color: '#31363a'
                                            }}>Data & History</span>
                                            <span className='float-end'>
                                                <Button
                                                    type='button'
                                                    text={`${isDataAndHistoryVisible ? '- Hide' : '+ Show'} Data & History`}
                                                    bootstrapClass='btn btn-link'
                                                    aria-controls="collapse-wrapper"
                                                    aria-expanded={isDataAndHistoryVisible}
                                                    onClick={onShowHideDataAndHistory}
                                                />
                                            </span>
                                        </div>

                                        <Collapse in={isDataAndHistoryVisible}>
                                            <div id='collapse-wrapper'>
                                                <div className='collapse-content'>
                                                    <div>
                                                        <Data
                                                            taskDetails={locationTaskDetails}
                                                            setLocationTaskDetails={setLocationTaskDetails}
                                                            isProjectWidgetActive={drawerFilters.isProjectWidgetActive}
                                                            isMobile={props.isMobile}
                                                        />
                                                    </div>

                                                    <div className='location-task-history'>
                                                        <div style={{
                                                            fontFamily: 'Greycliff',
                                                            fontWeight: '700',
                                                            fontSize: '16px',
                                                            lineHeight: '24px',
                                                            color: '#31363a',
                                                            marginBottom: '12px'
                                                        }}>
                                                            History
                                                        </div>
                                                        <div className='history-body'>
                                                            {(locationTaskHistory || []).map((h) => (
                                                                <HistoryRow key={h.id} historyItem={h} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </div>
                                }
                            </div>
                        </div>
                        {/* Data and History */}
                    </div>
                </div>
            }

            <div className='border-top location-task-sidebar-footer'>
                <Button type='button' text='Cancel' bootstrapClass='btn btn-link' onClick={props.closeDrawer} />
                <Button type='button' text='Save' bootstrapClass='btn btn-primary' onClick={saveLocationTaskChanges} />
            </div>

            {/* Feedback Modal */}
            <FeedbackModal
                {...{
                    feedbackModalRef,
                    selectedFeedbackOption,
                    setSelectedFeedbackOption,
                    feedbackText,
                    setFeedbackText,
                    handleCloseFeedbackModal,
                    hasBaselineTaskLink: locationTaskDetails?.links?.some(link => (link.baselineTaskLinkId ?? 0) > 0) || false
                }} locationTaskId={props.locationTaskId} />
        </>
    };

    return <Sidebar content={sidebarContent} visible={props.isDrawerVisible} position='right' onShow={onShowSidebar} onHide={onHideSidebar} className='col-12 col-lg-8 col-xl-7' />;
};

export default LocationTaskSidebar;
